import { useState, useEffect, useCallback } from "react";
import httpService from "V2.0/services/httpService";

import useFilterConversations from "./useFilterConversations";
import { CONVERSATIONS_PER_PAGE } from "../constants";

export function usePaginatedConversations(
  projectId,
  searchTerm,
  updateSelectedConversation
) {
  const {
    startDate,
    endDate,
    assistantId,
    extractedData,
    callDuration,
    assignedTags,
    untagged,
    updateAssistantId,
    updateDate,
    updateExtractedData,
    updateCallDuration,
    updateAssignedTags,
    updateUntagged,
    resetFilters,
  } = useFilterConversations(projectId);

  const [state, setState] = useState({
    totalConversations: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: CONVERSATIONS_PER_PAGE,
    conversations: [],
    loading: true,
    isLoadingMore: false,
    isReset: false,
  });

  const resetConversations = () =>
    setState({
      totalConversations: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: CONVERSATIONS_PER_PAGE,
      conversations: [],
      loading: true,
      isLoadingMore: false
    });

  const setLoading = (loading, isMoreLoading) =>
    setState((prevState) => ({
      ...prevState,
      loading,
      isMoreLoading
    }));

  const appendConversations = (data, isMoreLoading) =>
    setState((prevState) => ({
      ...data,
      conversations: [...prevState.conversations, ...data.conversations],
      isMoreLoading,
    }));

  const pushConversation = (conversation) => {
    setState((prevState) => ({
      ...prevState,
      conversations: [conversation, ...prevState.conversations],
    }));
  };

  const setConversations = (data, loading) =>
    setState({
      ...data,
      loading,
      pageSize: CONVERSATIONS_PER_PAGE,
      isReset: false,
    });

  const setSearchConversations = (data, loading) =>
    setState({
      totalConversations: data.conversations.length,
      conversations: data.conversations,
      loading,
    });

  const syncSelectedConversation = (selectedConversation) => {
    setState((prevState) => {
      const { conversations } = prevState;
      const updatedConversations = conversations.map((conversation) => {
        const { _id } = conversation;
        return _id === selectedConversation._id
          ? selectedConversation
          : conversation;
      });

      return {
        ...prevState,
        conversations: updatedConversations,
      };
    });
  };

  const fetchConversationsBySearchTerm = async () => {
    setLoading(true);

    try {
      const response = await httpService.get(
        `/conversation/searchConversations/${projectId}?searchTerm=${searchTerm}`
      );
      const data = response.data;

      setSearchConversations(data, false);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPaginatedConversations = useCallback(
    async (page = 1, pageSize = CONVERSATIONS_PER_PAGE, loading = true, isMoreLoading=false) => {
      // const { startDate, endDate, assistantId, extractedData } = params;
      setLoading(loading, isMoreLoading);

      const params = {
        page,
        pageSize,
        ...(Boolean(searchTerm) ? { searchTerm } : {}),
        ...(Boolean(startDate) ? { startDate } : {}),
        ...(Boolean(endDate) ? { endDate } : {}),
        ...(Boolean(assistantId) ? { assistantId } : {}),
        ...(Boolean(Object.keys(extractedData).length)
          ? { extractedData: JSON.stringify(extractedData) }
          : {}),
        ...(Number(callDuration) > 0 ? { callDuration: callDuration } : {}),
        ...(assignedTags.length > 0 && !untagged
          ? { assignedTags: JSON.stringify(assignedTags) }
          : {}),
        ...(assignedTags.length === 0 && untagged ? { untagged: true } : {}),
      };

      try {
        const response = await httpService.get(
          `/conversation/whitelabelGetPaginatedThreadsv2/${projectId}`,
          {
            params,
          }
        );

        if (response.status === 200) {
          const data = response.data;

          if (page === 1) {
            setConversations(data, false);
          } else {
            appendConversations(data, false);
          }
        }
      } catch (error) {
        setLoading(false, false);
        console.error(error);
      }
    },
    [
      startDate,
      endDate,
      extractedData,
      assistantId,
      untagged,
      assignedTags,
      callDuration,
      searchTerm,
      projectId,
    ]
  );

  const resetDate = () => {
    updateDate({ startDate: "", endDate: "" });
    setState((prev) => {
      return {
        ...prev,
        isReset: true,
      };
    });
  };

  const resetAssistantId = () => {
    updateAssistantId("");
    setState((prev) => {
      return {
        ...prev,
        isReset: true,
      };
    });
  };

  const resetExtractedData = () => {
    updateExtractedData({});
    setState((prev) => {
      return {
        ...prev,
        isReset: true,
      };
    });
  };

  const resetCallDuration = () => {
    updateCallDuration(0);
    setState((prev) => {
      return {
        ...prev,
        isReset: true,
      };
    });
  };

  const resetAssignedTags = () => {
    updateAssignedTags([]);
    setState((prev) => {
      return {
        ...prev,
        isReset: true,
      };
    });
  };

  const resetUntagged = () => {
    updateUntagged(false);
    setState((prev) => {
      return {
        ...prev,
        isReset: true,
      };
    });
  };

  const loadMoreConversations = useCallback(() => {
    if (state.currentPage < state.totalPages) {
      fetchPaginatedConversations(state.currentPage + 1, state.pageSize, false, true);
    }
  }, [state.currentPage, state.pageSize, state.totalPages]);

  const resetAppliedFilters = () => {
    resetFilters();
    setState((prevState) => {
      return {
        ...prevState,
        isReset: true,
      };
    });
  };

  const showLoadMoreConversation =
    !state.loading &&
    state.currentPage != state.totalPages &&
    state.totalPages != 0;

  useEffect(() => {
    if (state.isReset) {
      fetchPaginatedConversations(1, CONVERSATIONS_PER_PAGE, true);
      updateSelectedConversation({});
    }
  }, [state.isReset]);

  return {
    ...state,
    fetchPaginatedConversations,
    loadMoreConversations,
    showLoadMoreConversation,
    fetchConversationsBySearchTerm,
    resetConversations,
    startDate,
    endDate,
    assistantId,
    extractedData,
    callDuration,
    assignedTags,
    untagged,
    updateAssistantId,
    updateDate,
    updateExtractedData,
    updateCallDuration,
    updateAssignedTags,
    updateUntagged,
    resetDate,
    resetExtractedData,
    resetAssistantId,
    resetCallDuration,
    resetAssignedTags,
    resetUntagged,
    resetFilters: resetAppliedFilters,
    syncSelectedConversation,
    pushConversation,
  };
}
